import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { IDetailDisplayItem } from '../detail/detail-list/detail-list.component';
import { INotesDialogData, NotesDialogComponent } from '../notes-dialog/notes-dialog.component';
import { LabpartnerService } from '../services/labpartner.service';
import { Detail, Experiment, Note } from '../services/labpartner.service.model';
import { NotesService } from '../services/notes-service.service';
import { BaseComponent } from '../support/base.component';

@Component({
  selector: 'app-note-display',
  templateUrl: './note-display.component.html',
  styleUrls: ['./note-display.component.scss'],
  standalone: false,
})
export class NoteDisplayComponent extends BaseComponent implements OnChanges, OnDestroy {
  @Input() noteCount?: number | null;
  @Input() experimentId?: number | null = 0;
  @Input() objectID!: number;
  @Input() objectName!: string;
  @Input() isEditMode: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() selectedDetails: IDetailDisplayItem[] = [];
  @Input() experimentDetail?: Detail;
  @Input() hasLiveNote?: boolean;

  liveNoteCountApplied: boolean = false;

  notes?: Note[];
  liveNote?: Note;
  currentNote!: Note;

  constructor(
    private notesService: NotesService,
    private dialog: MatDialog,
    private apiService: LabpartnerService,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode && !(changes.objectID || changes.objectName)) {
      return;
    }
    this.getNotes();
  }

  private getNotes() {
    if (this.notes && this.notes.length > 0) {
      this.currentNote = this.notes[0];
      this.noteCount = Math.max(this.noteCount ?? 0, this.notes.length);
    }
  }

  ngOnInit(): void {}

  protected ngOnDestroyInternal(): void {}

  onNote() {
    if (this.experimentId === null) {
      this.apiService
        .getNotesList('ObjectID', this.objectID, 'AssayNameLiveNote')
        .subscribe(liveNotes => {
          if (liveNotes) {
            this.liveNote = liveNotes[0];
          }
          this.openNote();
        });
    } else {
      this.apiService
        .getNotesList('ObjectID', this.objectID, this.objectName, this.experimentId)
        .pipe(
          concatMap(notes => {
            this.notes = notes;

            if (this.objectName === 'Detail') {
              return this.apiService.getNotesList('ObjectID', this.objectID, 'DetailLiveNote', this.experimentId);
            }
            if (this.objectName === 'Cassette') {
              return this.apiService.getNotesList(
                'ObjectID',
                this.objectID,
                'CassetteLiveNote',
                this.experimentId,
              );
            }
            return of(undefined);
          }),
        )
        .subscribe(liveNotes => {
          if (liveNotes) {
            this.liveNote = liveNotes[0];
          }

          this.openNote();
        });
    }
  }

  getNoteCounter() {
    let noteCount = 0;
    if (this.noteCount) {
      noteCount += this.noteCount;
    }
    if (this.hasLiveNote) {
      noteCount++;
    }
    return noteCount;
  }

  private openNote() {
    if (this.experimentId == null) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.panelClass = 'add-view-notes-dialog';
      dialogConfig.disableClose = false;
      dialogConfig.autoFocus = true;
      dialogConfig.width = '60%';
      dialogConfig.data = <INotesDialogData>{
        experimentId: this.experimentId,
        notes: this.notes,
        liveNote: this.liveNote,
        objectId: this.objectID,
        objectName: this.objectName,
        selectedDetails: this.selectedDetails,
        experimentDetail: this.experimentDetail,
        experiment: null,
      };

      const dialogRef = this.dialog.open(NotesDialogComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((dialogResult: { ok: boolean; data: any }) => {
        if (dialogResult && dialogResult.ok) {
          this.hasLiveNote = true;
        }
      });
    } else {
      this.subscription.add(
        this.apiService.getExperiment(this.experimentId).subscribe((experiment: Experiment) => {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.panelClass = 'add-view-notes-dialog';
          dialogConfig.disableClose = false;
          dialogConfig.autoFocus = true;
          dialogConfig.width = '60%';
          dialogConfig.data = <INotesDialogData>{
            experimentId: this.experimentId,
            notes: this.notes,
            liveNote: this.liveNote,
            objectId: this.objectID,
            objectName: this.objectName,
            selectedDetails: this.selectedDetails,
            experimentDetail: this.experimentDetail,
            experiment: experiment,
          };

          const dialogRef = this.dialog.open(NotesDialogComponent, dialogConfig);

          dialogRef
            .afterClosed()
            .subscribe((dialogResult: { ok: boolean; data: any; liveNote?: boolean }) => {
              if (dialogResult?.liveNote) {
                this.hasLiveNote = true;
              }
              if (dialogResult && dialogResult.ok && this.experimentId) {
                this.apiService
                  .getNoteCountsByExperimentId(this.experimentId)
                  .subscribe(experimentNoteCounts => {
                    this.notesService.experimentNotesCountMap.next(experimentNoteCounts);
                    this.getNotes();
                  });
              }
            });
        }),
      );
    }
  }
}
